/**
 * Hook for managing user preferences (persona selection)
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import type {
  PersonaId,
  Persona,
  UserPreferencesResponse,
  UpdatePreferencesResponse,
} from "../types/persona";

const PREFERENCES_QUERY_KEY = ["user", "preferences"];
const PERSONAS_QUERY_KEY = ["personas"];

/**
 * Fetch user preferences from API
 */
async function fetchUserPreferences(): Promise<UserPreferencesResponse> {
  const response = await fetch("/api/user/preferences", {
    credentials: "include",
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Not authenticated");
    }
    throw new Error("Failed to fetch preferences");
  }

  return response.json();
}

/**
 * Fetch available personas from API
 */
async function fetchPersonas(): Promise<Persona[]> {
  const response = await fetch("/api/personas");

  if (!response.ok) {
    throw new Error("Failed to fetch personas");
  }

  const data: Persona[] = await response.json();
  return data;
}

/**
 * Update user's persona preference
 */
async function updatePersona(personaId: PersonaId): Promise<UpdatePreferencesResponse> {
  const response = await fetch("/api/user/preferences", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ personaId }),
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? "Failed to update preferences");
  }

  return response.json();
}

/**
 * Complete onboarding with persona selection
 */
async function completeOnboarding(personaId: PersonaId): Promise<UpdatePreferencesResponse> {
  const response = await fetch("/api/user/preferences/complete-onboarding", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ personaId }),
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? "Failed to complete onboarding");
  }

  return response.json();
}

/**
 * Hook for user preferences
 */
export function useUserPreferences() {
  const queryClient = useQueryClient();

  // Query for user preferences
  const preferencesQuery = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: fetchUserPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Query for available personas
  const personasQuery = useQuery({
    queryKey: PERSONAS_QUERY_KEY,
    queryFn: fetchPersonas,
    staleTime: Infinity, // Personas don't change
  });

  // Mutation for updating persona
  const updatePersonaMutation = useMutation({
    mutationFn: updatePersona,
    onSuccess: (data) => {
      queryClient.setQueryData(PREFERENCES_QUERY_KEY, data.preferences);
    },
  });

  // Mutation for completing onboarding
  const completeOnboardingMutation = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: (data) => {
      queryClient.setQueryData(PREFERENCES_QUERY_KEY, data.preferences);
    },
  });

  return {
    // Preferences data
    preferences: preferencesQuery.data,
    isLoadingPreferences: preferencesQuery.isLoading,
    preferencesError: preferencesQuery.error,

    // Personas data
    personas: personasQuery.data ?? [],
    isLoadingPersonas: personasQuery.isLoading,

    // Actions
    updatePersona: updatePersonaMutation.mutate,
    isUpdatingPersona: updatePersonaMutation.isPending,

    completeOnboarding: completeOnboardingMutation.mutate,
    isCompletingOnboarding: completeOnboardingMutation.isPending,

    // Computed
    needsOnboarding: preferencesQuery.data?.onboardingCompleted === false,
    currentPersonaId: preferencesQuery.data?.personaId ?? "akela",
  };
}
